import { useCallback, useEffect, useRef, useState } from 'react';
import { createExpense, deleteExpense, loadExpenses, updateExpense } from '../services/expenseData';

// Supabase is the source of truth: every write is followed by a reload, writes run
// one at a time, and only the newest load is allowed to update state.
function useExpenses({ workspaceId, userId }) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [refreshError, setRefreshError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const generation = useRef(0);
  const queue = useRef(Promise.resolve());

  useEffect(() => {
    generation.current += 1;
    const current = generation.current;
    if (!workspaceId || !userId) {
      setItems([]);
      setStatus('idle');
      setError('');
      setRefreshError('');
      return undefined;
    }

    setStatus('loading');
    setError('');
    setRefreshError('');
    loadExpenses(workspaceId)
      .then((rows) => {
        if (generation.current !== current) return;
        setItems(rows);
        setStatus('ready');
      })
      .catch((requestError) => {
        if (generation.current !== current) return;
        setError(requestError.message || 'Unable to load expenses.');
        setStatus('error');
      });

    return () => {
      generation.current += 1;
    };
  }, [workspaceId, userId, reloadToken]);

  const reload = useCallback(() => setReloadToken((value) => value + 1), []);

  const mutate = useCallback((operation) => {
    if (!workspaceId || !userId) {
      return Promise.resolve({ saved: false, error: 'Your workspace is not ready yet.' });
    }

    const task = queue.current.then(async () => {
      try {
        await operation(workspaceId, userId);
      } catch (requestError) {
        return { saved: false, error: requestError.message || 'Your change could not be saved.' };
      }

      generation.current += 1;
      const current = generation.current;
      try {
        const rows = await loadExpenses(workspaceId);
        if (generation.current === current) {
          setItems(rows);
          setStatus('ready');
          setRefreshError('');
        }
        return { saved: true, refreshed: true };
      } catch (requestError) {
        const message = requestError.message || 'The expenses could not refresh.';
        if (generation.current === current) setRefreshError(message);
        return { saved: true, refreshed: false, refreshError: message };
      }
    });
    queue.current = task.catch(() => undefined);
    return task;
  }, [workspaceId, userId]);

  const addExpense = useCallback((values) => mutate((workspace, owner) => (
    createExpense(workspace, owner, values)
  )), [mutate]);

  const editExpense = useCallback((id, values) => mutate((workspace) => (
    updateExpense(workspace, id, values)
  )), [mutate]);

  const removeExpense = useCallback((id) => mutate((workspace) => (
    deleteExpense(workspace, id)
  )), [mutate]);

  return { items, status, error, refreshError, reload, addExpense, editExpense, removeExpense };
}

export default useExpenses;